import { api } from './client'
import type {
  CdDiff,
  CdObservation,
  CdObservationPayload,
  CdProject,
  CdProjectPayload,
  CdReport,
  CdReportFormat,
} from '../types/changeDetection'

interface AbDiffPayload {
  name: string
  sourceObservationId: number
  targetObservationId: number
  areaWkt?: string | null
}

interface TimeSeriesDiffPayload {
  name: string
  areaWkt?: string | null
}

export const cdApi = {
  // 프로젝트
  listProjects: () =>
    api.get<CdProject[]>('/change-detection/projects').then((r) => r.data),
  createProject: (data: CdProjectPayload) =>
    api.post<CdProject>('/change-detection/projects', data).then((r) => r.data),
  getProject: (id: number) =>
    api.get<CdProject>(`/change-detection/projects/${id}`).then((r) => r.data),
  updateProject: (id: number, data: CdProjectPayload) =>
    api.put<CdProject>(`/change-detection/projects/${id}`, data).then((r) => r.data),
  deleteProject: (id: number) => api.delete(`/change-detection/projects/${id}`),

  uploadRegionGpkg: (id: number, file: File) => {
    const fd = new FormData()
    fd.append('file', file)
    return api
      .post<CdProject>(`/change-detection/projects/${id}/region-gpkg`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then((r) => r.data)
  },
  deleteRegionGpkg: (id: number) =>
    api.delete<CdProject>(`/change-detection/projects/${id}/region-gpkg`).then((r) => r.data),

  // 관측
  listObservations: (projectId: number) =>
    api
      .get<CdObservation[]>(`/change-detection/projects/${projectId}/observations`)
      .then((r) => r.data),
  addObservation: (projectId: number, data: CdObservationPayload) =>
    api
      .post<CdObservation>(`/change-detection/projects/${projectId}/observations`, data)
      .then((r) => r.data),
  getObservation: (id: number) =>
    api.get<CdObservation>(`/change-detection/observations/${id}`).then((r) => r.data),
  deleteObservation: (id: number) =>
    api.delete(`/change-detection/observations/${id}`),
  revoxelize: (id: number) =>
    api.post<CdObservation>(`/change-detection/observations/${id}/voxelize`).then((r) => r.data),

  // 변화탐지(diff)
  listDiffs: (projectId: number) =>
    api.get<CdDiff[]>(`/change-detection/projects/${projectId}/diffs`).then((r) => r.data),
  createAbDiff: (projectId: number, data: AbDiffPayload) =>
    api
      .post<CdDiff>(`/change-detection/projects/${projectId}/diffs/ab`, data)
      .then((r) => r.data),
  createTimeSeriesDiff: (projectId: number, data: TimeSeriesDiffPayload) =>
    api
      .post<CdDiff>(`/change-detection/projects/${projectId}/diffs/time-series`, data)
      .then((r) => r.data),
  getDiff: (id: number) =>
    api.get<CdDiff>(`/change-detection/diffs/${id}`).then((r) => r.data),
  deleteDiff: (id: number) => api.delete(`/change-detection/diffs/${id}`),
  cancelDiff: (id: number) =>
    api.post<CdDiff>(`/change-detection/diffs/${id}/cancel`).then((r) => r.data),

  // 리포트 (PDF / HWPX)
  listReports: (diffId: number) =>
    api.get<CdReport[]>(`/change-detection/diffs/${diffId}/reports`).then((r) => r.data),
  createReport: (diffId: number, data: { format: CdReportFormat; screenshotBase64?: string | null }) =>
    api
      .post<CdReport>(`/change-detection/diffs/${diffId}/reports`, data)
      .then((r) => r.data),
  deleteReport: (diffId: number, reportId: number) =>
    api.delete(`/change-detection/diffs/${diffId}/reports/${reportId}`),
  downloadReport: async (diffId: number, reportId: number, fileName: string) => {
    const res = await api.get(
      `/change-detection/diffs/${diffId}/reports/${reportId}/download`,
      { responseType: 'blob' },
    )
    const url = window.URL.createObjectURL(res.data)
    const a = document.createElement('a')
    a.href = url
    a.download = fileName
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    window.URL.revokeObjectURL(url)
  },
}
